import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  fetchAllChangeRequests, 
  updateChangeRequest,
  fetchTenants 
} from '../../services/pocketbase';
import { format } from 'date-fns';
import { CHANGE_REQUEST_STATUSES, PRIORITIES } from '../../config/constants';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiTool, FiEye, FiEdit3, FiX, FiSave, FiFilter, FiSearch } = FiIcons;

const AdminChangeRequests = () => {
  const [requests, setRequests] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [selectedRequest, setSelectedRequest] = useState(null);
  const [editingRequest, setEditingRequest] = useState(null);
  const [filters, setFilters] = useState({
    status: [],
    priority: [],
    tenant: ''
  });
  const [searchTerm, setSearchTerm] = useState('');
  const [formData, setFormData] = useState({
    status: '',
    internal_note: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [requests, filters, searchTerm]);

  const fetchData = async () => {
    try {
      setIsLoading(true);
      const [requestsData, tenantsData] = await Promise.all([
        fetchAllChangeRequests(),
        fetchTenants()
      ]);
      setRequests(requestsData);
      setTenants(tenantsData);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...requests];

    if (filters.status.length > 0) {
      filtered = filtered.filter(r => filters.status.includes(r.status));
    }

    if (filters.priority.length > 0) {
      filtered = filtered.filter(r => filters.priority.includes(r.priority));
    }

    if (filters.tenant) {
      filtered = filtered.filter(r => r.tenant === filters.tenant);
    }

    if (searchTerm) {
      filtered = filtered.filter(r =>
        r.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        r.expand?.tenant?.business_name.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    setFilteredRequests(filtered);
  };

  const handleFilter = (type, value) => {
    const newFilters = { ...filters };
    if (newFilters[type].includes(value)) {
      newFilters[type] = newFilters[type].filter(item => item !== value);
    } else {
      newFilters[type] = [...newFilters[type], value];
    }
    setFilters(newFilters);
  };

  const handleStatusUpdate = async (requestId, newStatus) => {
    try {
      await updateChangeRequest(requestId, { status: newStatus });
      setRequests(requests.map(r => 
        r.id === requestId ? { ...r, status: newStatus } : r
      ));
      setSuccess('Status updated successfully');
      setEditingRequest(null);

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleNoteUpdate = async (e) => {
    e.preventDefault();
    
    try {
      await updateChangeRequest(editingRequest.id, { 
        status: formData.status,
        internal_note: formData.internal_note 
      });
      
      setRequests(requests.map(r => 
        r.id === editingRequest.id ? { 
          ...r, 
          status: formData.status,
          internal_note: formData.internal_note 
        } : r
      ));
      
      setSuccess('Request updated successfully');
      setEditingRequest(null);
      setFormData({ status: '', internal_note: '' });

      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(err.message);
    }
  };

  const handleEdit = (request) => {
    setEditingRequest(request);
    setFormData({
      status: request.status,
      internal_note: request.internal_note || ''
    });
  };

  const getStatusBadgeColor = (status) => {
    const colors = {
      'new': 'bg-blue-100 text-blue-800',
      'in_progress': 'bg-yellow-100 text-yellow-800',
      'completed': 'bg-green-100 text-green-800',
      'rejected': 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPriorityBadgeColor = (priority) => {
    const colors = {
      'low': 'bg-gray-100 text-gray-800',
      'normal': 'bg-blue-100 text-blue-800',
      'high': 'bg-red-100 text-red-800'
    };
    return colors[priority] || 'bg-gray-100 text-gray-800';
  };

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="animate-pulse">
          <div className="h-8 bg-gray-200 rounded mb-6 w-48"></div>
          <div className="bg-gray-200 h-64 rounded-lg"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <h1 className="text-2xl font-bold text-gray-900 mb-6">Change Requests Management</h1>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {success && (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            className="bg-green-50 border border-green-200 rounded-md p-4 mb-6"
          >
            <p className="text-green-700">{success}</p>
          </motion.div>
        )}

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <SafeIcon icon={FiSearch} className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search requests..."
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </div>

            <div>
              <select
                value={filters.tenant}
                onChange={(e) => setFilters({ ...filters, tenant: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              >
                <option value="">All Tenants</option>
                {tenants.map(tenant => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.business_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Status Filter
              </label>
              <div className="flex flex-wrap gap-1">
                {CHANGE_REQUEST_STATUSES.map(status => (
                  <button
                    key={status.value}
                    onClick={() => handleFilter('status', status.value)}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      filters.status.includes(status.value)
                        ? 'bg-blue-100 text-blue-800 border-blue-300'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {status.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Priority Filter
              </label>
              <div className="flex flex-wrap gap-1">
                {PRIORITIES.map(priority => (
                  <button
                    key={priority.value}
                    onClick={() => handleFilter('priority', priority.value)}
                    className={`px-2 py-1 text-xs rounded border transition-colors ${
                      filters.priority.includes(priority.value)
                        ? 'bg-blue-100 text-blue-800 border-blue-300'
                        : 'bg-gray-100 text-gray-700 border-gray-300 hover:bg-gray-200'
                    }`}
                  >
                    {priority.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Edit Request Modal */}
        <AnimatePresence>
          {editingRequest && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Update Request</h2>
                    <button
                      onClick={() => setEditingRequest(null)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="mb-6">
                    <h3 className="text-lg font-medium text-gray-900 mb-2">{editingRequest.title}</h3>
                    <p className="text-sm text-gray-600">
                      From: {editingRequest.expand?.tenant?.business_name}
                    </p>
                    <p className="text-sm text-gray-600">
                      Submitted: {format(new Date(editingRequest.created), 'MMM dd, yyyy HH:mm')}
                    </p>
                  </div>

                  <form onSubmit={handleNoteUpdate} className="space-y-6">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Status
                      </label>
                      <select
                        value={formData.status}
                        onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      >
                        {CHANGE_REQUEST_STATUSES.map(status => (
                          <option key={status.value} value={status.value}>
                            {status.label}
                          </option>
                        ))}
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Internal Note (Not visible to client)
                      </label>
                      <textarea
                        value={formData.internal_note}
                        onChange={(e) => setFormData({ ...formData, internal_note: e.target.value })}
                        rows={4}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                        placeholder="Add internal notes about this request..."
                      />
                    </div>

                    <div className="flex items-center justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => setEditingRequest(null)}
                        className="px-4 py-2 text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
                      >
                        Cancel
                      </button>
                      <motion.button
                        whileHover={{ scale: 1.02 }}
                        whileTap={{ scale: 0.98 }}
                        type="submit"
                        className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                      >
                        <SafeIcon icon={FiSave} className="w-4 h-4" />
                        <span>Update Request</span>
                      </motion.button>
                    </div>
                  </form>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Request Detail Modal */}
        <AnimatePresence>
          {selectedRequest && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4"
            >
              <motion.div
                initial={{ scale: 0.95, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.95, opacity: 0 }}
                className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
              >
                <div className="p-6">
                  <div className="flex items-center justify-between mb-6">
                    <h2 className="text-xl font-semibold text-gray-900">Request Details</h2>
                    <button
                      onClick={() => setSelectedRequest(null)}
                      className="p-2 text-gray-400 hover:text-gray-600"
                    >
                      <SafeIcon icon={FiX} className="w-5 h-5" />
                    </button>
                  </div>

                  <div className="space-y-4">
                    <div>
                      <h3 className="text-lg font-medium text-gray-900 mb-2">{selectedRequest.title}</h3>
                      <div className="flex items-center space-x-4 mb-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(selectedRequest.status)}`}>
                          {CHANGE_REQUEST_STATUSES.find(s => s.value === selectedRequest.status)?.label || selectedRequest.status}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeColor(selectedRequest.priority)}`}>
                          {PRIORITIES.find(p => p.value === selectedRequest.priority)?.label || selectedRequest.priority}
                        </span>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Client:</label>
                      <p className="text-sm text-gray-900">{selectedRequest.expand?.tenant?.business_name}</p>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Requested by:</label>
                      <p className="text-sm text-gray-900">{selectedRequest.expand?.requested_by?.email}</p>
                    </div>

                    {selectedRequest.page_url && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Page/Reference:</label>
                        <p className="text-sm text-blue-600 break-all">{selectedRequest.page_url}</p>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description:</label>
                      <div className="bg-gray-50 p-4 rounded-md">
                        <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRequest.description}</p>
                      </div>
                    </div>

                    {selectedRequest.internal_note && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Internal Note:</label>
                        <div className="bg-yellow-50 p-4 rounded-md border border-yellow-200">
                          <p className="text-sm text-gray-900 whitespace-pre-wrap">{selectedRequest.internal_note}</p>
                        </div>
                      </div>
                    )}

                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Submitted:</label>
                      <p className="text-sm text-gray-600">{format(new Date(selectedRequest.created), 'MMM dd, yyyy HH:mm')}</p>
                    </div>

                    <div className="flex items-center justify-end space-x-3 pt-4">
                      <button
                        onClick={() => handleEdit(selectedRequest)}
                        className="flex items-center space-x-2 bg-blue-600 text-white px-4 py-2 rounded-md hover:bg-blue-700"
                      >
                        <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                        <span>Update Request</span>
                      </button>
                    </div>
                  </div>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Requests List */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          {filteredRequests.length === 0 ? (
            <div className="p-8 text-center">
              <SafeIcon icon={FiTool} className="w-12 h-12 text-gray-300 mx-auto mb-4" />
              <p className="text-gray-500">
                {requests.length === 0 ? 'No change requests found' : 'No requests match your filters'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Request
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Client
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Status
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Priority
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Date
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredRequests.map((request) => (
                    <motion.tr
                      key={request.id}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className="hover:bg-gray-50"
                    >
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm font-medium text-gray-900">{request.title}</p>
                          {request.page_url && (
                            <p className="text-xs text-gray-500 truncate max-w-xs">{request.page_url}</p>
                          )}
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="text-sm text-gray-900">{request.expand?.tenant?.business_name}</p>
                          <p className="text-xs text-gray-500">{request.expand?.requested_by?.email}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <button
                          onClick={() => {
                            const newStatus = request.status === 'new' ? 'in_progress' : 
                                           request.status === 'in_progress' ? 'completed' : request.status;
                            if (newStatus !== request.status) {
                              handleStatusUpdate(request.id, newStatus);
                            }
                          }}
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusBadgeColor(request.status)} hover:opacity-80 transition-opacity`}
                        >
                          {CHANGE_REQUEST_STATUSES.find(s => s.value === request.status)?.label || request.status}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getPriorityBadgeColor(request.priority)}`}>
                          {PRIORITIES.find(p => p.value === request.priority)?.label || request.priority}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-900">
                        {format(new Date(request.created), 'MMM dd, yyyy')}
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => setSelectedRequest(request)}
                            className="p-2 text-gray-400 hover:text-blue-600"
                            title="View details"
                          >
                            <SafeIcon icon={FiEye} className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleEdit(request)}
                            className="p-2 text-gray-400 hover:text-green-600"
                            title="Update request"
                          >
                            <SafeIcon icon={FiEdit3} className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </motion.tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default AdminChangeRequests;